/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import * as drive from "../../../api/drive";
import { formatBytes } from "./format";

/**
 * Unauthenticated view for a public share token. Deliberately outside
 * ProtectedRoute — the whole point is that a recipient without an account can
 * open it. The API embeds a signed download URL in the response, so no
 * credentials are needed to fetch the bytes either.
 */
const PublicSharePage = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let active = true;
    drive
      .resolvePublicLink(token)
      .then((data) => active && setState({ loading: false, data, error: null }))
      .catch((err) =>
        active &&
        setState({ loading: false, data: null, error: err.message || "This link is not valid." })
      );
    return () => {
      active = false;
    };
  }, [token]);

  if (state.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: "accent.main" }} />
      </Box>
    );
  }

  if (state.error) {
    return (
      <Box sx={{ maxWidth: 640, mx: "auto", mt: 8, px: 2 }}>
        <Alert severity="error">{state.error}</Alert>
      </Box>
    );
  }

  const { data } = state;
  const files = data.type === "file" ? [data.file] : data.files;
  const title = data.type === "file" ? data.file.file_name : data.folder.folder_name;

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", mt: 6, px: 2 }}>
      <Typography variant="h5" gutterBottom>{title}</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Shared with you · read-only
      </Typography>
      <Paper variant="outlined" sx={{ mt: 2 }}>
        <List disablePadding>
          {files.map((file, index) => (
            <Box key={file.id}>
              {index > 0 && <Divider />}
              <ListItem
                secondaryAction={
                  <Button
                    startIcon={<DownloadIcon />}
                    href={file.download_url}
                    download={file.file_name}
                    rel="noopener"
                    sx={{ color: "accent.main", textTransform: "none" }}
                  >
                    Download
                  </Button>
                }
              >
                <ListItemText primary={file.file_name} secondary={formatBytes(file.size_bytes)} />
              </ListItem>
            </Box>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default PublicSharePage;
